<template>
  <div class="members-table-wrapper">
    <Loader v-if="loading" />
    
    <table class="members-table">
      <thead class="members-table__head">
        <tr>
          <th 
            v-for="(header, index) in tableHeaders" 
            :key="'header' + index"
            :class="{ 'members-table__th--hidden': header === 'id' }"
            class="members-table__th"
          >
            {{ header }}
          </th>
        </tr>
      </thead>
      <tbody class="members-table__body">
        <tr 
          v-for="(details, rowIndex) in tableRows" 
          :key="'row' + rowIndex"
          class="members-table__row"
          :ref="rowDetails"
        >
          <td 
            v-for="(detail, colIndex) in details" 
            :key="'col' + colIndex"
            :class="{ 'members-table__td--hidden': detail.includes && detail.includes('data-id') }"
            class="members-table__td"
          >
            <span 
              v-html="detail" 
              class="members-table__cell-content"
              :class="getStatusClass(detail)"
              :ref="el => setStatusRef(el, detail)"
            ></span>
          </td>
          <td class="members-table__td members-table__td--action">
            <div v-if="details.status" class="action-menu">
              <button class="action-menu__trigger">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="1" fill="currentColor"/>
                  <circle cx="19" cy="12" r="1" fill="currentColor"/>
                  <circle cx="5" cy="12" r="1" fill="currentColor"/>
                </svg>
              </button>
              <div class="action-menu__dropdown">
                <button
                  v-if="details.status === 'Deactivated'"
                  class="action-menu__item action-menu__item--success"
                  @click="activateAMember($event, details.id)"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Activate member
                </button>
                <button
                  v-if="details.status === 'Active' && deals.length !== 0"
                  class="action-menu__item"
                  @click="openSendDealModal(details)"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M22 2L11 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Send deal
                </button>
                <button
                  v-if="details.status === 'Active'"
                  class="action-menu__item action-menu__item--danger"
                  @click="deActivateAMember($event, details.id)"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                    <path d="M15 9L9 15M9 9L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Deactivate member
                </button>
                <button
                  v-if="details.status === 'Pending'"
                  class="action-menu__item action-menu__item--success"
                  @click="acceptMember($event, details.id)"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Accept
                </button>
                <button
                  v-if="details.status === 'Pending'"
                  class="action-menu__item action-menu__item--danger"
                  @click="declineMember($event, details.id)"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                    <path d="M15 9L9 15M9 9L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  Decline
                </button>
              </div>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="showPagination && totalPage > 1" class="members-table__pagination">
      <Pagination
        :totalPage="totalPage"
        :currentPage="currentPage"
        :maxVisible="9"
        @pageChange="gotoPage"
      />
    </div>

    <!-- Modals -->
    <SendDeal
      v-if="showTransferTokenModal"
      :show="showTransferTokenModal"
      :member="member"
      :deals="deals"
      @close-send-deal="showTransferTokenModal = false"
    />
    
    <ActivateModal
      v-if="showActivateModal"
      class="tw-z-20"
      @close="showActivateModal = !showActivateModal"
    >
      <template v-slot:content>
        <div class="modal-content">
          <div class="modal-content__icon modal-content__icon--success">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h3 class="modal-content__title">Activate member?</h3>
          <p class="modal-content__description">
            This will give this user access to participate in your deal room.
          </p>
          <div class="modal-content__actions">
            <button class="btn btn--primary btn--full" @click="activateAMember">
              <span v-if="!activateLoading">Yes, Activate</span>
              <img v-show="activateLoading" class="btn__spinner" src="@/assets/img/spinner.svg" alt="loading" />
            </button>
            <button class="btn btn--ghost" @click="showActivateModal = !showActivateModal">
              No, nevermind
            </button>
          </div>
        </div>
      </template>
    </ActivateModal>
    
    <DeactivateModal
      v-if="showDeActivateModal"
      class="tw-z-20"
      @close="showDeActivateModal = !showDeActivateModal"
    >
      <template v-slot:content>
        <div class="modal-content">
          <div class="modal-content__icon modal-content__icon--danger">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
              <path d="M15 9L9 15M9 9L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h3 class="modal-content__title">Deactivate member?</h3>
          <p class="modal-content__description">
            This will remove this user from deals and deactivate their account.
          </p>
          <div class="modal-content__actions">
            <button class="btn btn--danger btn--full" @click="deActivateAMember">
              <span v-if="!deActivateLoading">Yes, Deactivate</span>
              <img v-show="deActivateLoading" class="btn__spinner" src="@/assets/img/spinner.svg" alt="loading" />
            </button>
            <button class="btn btn--ghost" @click="showDeActivateModal = !showDeActivateModal">
              No, never mind
            </button>
          </div>
        </div>
      </template>
    </DeactivateModal>
    
    <AcceptModal
      v-if="showAcceptModal"
      class="tw-z-20"
      @close="showAcceptModal = !showAcceptModal"
    >
      <template v-slot:content>
        <div class="modal-content">
          <div class="modal-content__icon modal-content__icon--success">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <circle cx="8.5" cy="7" r="4" stroke="currentColor" stroke-width="2"/>
              <path d="M20 8V14M17 11H23" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h3 class="modal-content__title">Accept user?</h3>
          <p class="modal-content__description">
            This will add this user to your deal room.
          </p>
          <div class="modal-content__actions">
            <button class="btn btn--primary btn--full" @click="acceptMember">
              <span v-if="!acceptLoading">Yes, Accept</span>
              <img v-show="acceptLoading" class="btn__spinner" src="@/assets/img/spinner.svg" alt="loading" />
            </button>
            <button class="btn btn--ghost" @click="showAcceptModal = !showAcceptModal">
              No, nevermind
            </button>
          </div>
        </div>
      </template>
    </AcceptModal>
    
    <DeclineModal
      v-if="showDeclineModal"
      class="tw-z-20"
      @close="showDeclineModal = !showDeclineModal"
    >
      <template v-slot:content>
        <div class="modal-content">
          <div class="modal-content__icon modal-content__icon--danger">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <circle cx="8.5" cy="7" r="4" stroke="currentColor" stroke-width="2"/>
              <path d="M20 8L17 11M17 8L20 11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <h3 class="modal-content__title">Decline user?</h3>
          <p class="modal-content__description">
            User will not be added to your deal room.
          </p>
          <div class="modal-content__actions">
            <button class="btn btn--danger btn--full" @click="declineMember">
              <span v-if="!declineLoading">Yes, Decline</span>
              <img v-show="declineLoading" class="btn__spinner" src="@/assets/img/spinner.svg" alt="loading" />
            </button>
            <button class="btn btn--ghost" @click="showDeclineModal = !showDeclineModal">
              No, never mind
            </button>
          </div>
        </div>
      </template>
    </DeclineModal>
  </div>
</template>

<script>
  import { mapActions } from "vuex";
  import DeactivateModal from "@/components/general/SmallModal";
  import ActivateModal from "@/components/general/SmallModal";
  import AcceptModal from "@/components/general/SmallModal";
  import DeclineModal from "@/components/general/SmallModal";
  import TransferTokenModal from "@/components/general/MediumModal";
  import Pagination from "@/components/navigation/Pagination";
  import SendDeal from "@/components/members/SendDeal";

  export default {
    name: "MembersTable",

    components: {
      Pagination,
      DeactivateModal,
      ActivateModal,
      AcceptModal,
      DeclineModal,
      TransferTokenModal,
      SendDeal,
    },

    props: {
      tableHeaders: { type: Array, default: () => [], required: true },
      tableRows: { type: Array, default: () => [], required: true },
      columnNumber: { type: Number, default: () => 0, required: true },
      columnWidth: { type: Number, default: () => 0, required: true },
      totalPage: { type: Number, default: () => 0, required: true },
      currentPage: { type: Number, default: () => 0, required: true },
      deals: { type: Array, default: () => [], required: true },
      showPagination: {
        type: Boolean,
        required: false,
        default: () => true,
      },
    },

    emits: ["gotoPage", "update-members"],

    data() {
      return {
        rows: [],
        type: "",
        colunmSize: "",
        id: "",
        member: {},
        loading: false,
        activateLoading: false,
        deActivateLoading: false,
        acceptLoading: false,
        declineLoading: false,
        dealsLoading: false,
        showActivateModal: false,
        showDeActivateModal: false,
        showAcceptModal: false,
        showDeclineModal: false,
        showTransferTokenModal: false,
      };
    },

    mounted() {
      this.setTableColumn();
    },

    methods: {
      ...mapActions("organisationModule", [
        "deActivateMember",
        "activateMember",
        "pendingInvite",
      ]),

      rowDetails(el) {
        this.rows.push(el);
      },

      async openSendDealModal(member) {
        this.showTransferTokenModal = true;
        this.dealsLoading = true;
        this.member = member;
        return;
      },

      async activateAMember(event, memberId) {
        if (event.target.innerText === "Activate member") {
          this.id = memberId.split("/")[1];
          this.showActivateModal = true;
          return;
        }
        this.activateLoading = true;
        const response = await this.activateMember(this.id);
        if (response) {
          this.$emit("update-members");
        }
        this.activateLoading = false;
        this.showActivateModal = false;
      },

      async deActivateAMember(event, memberId) {
        if (event.target.innerText === "Deactivate member") {
          this.id = memberId.split("/")[1];
          this.showDeActivateModal = true;
          return;
        }
        this.deActivateLoading = true;
        const response = await this.deActivateMember(this.id);
        if (response) {
          this.$emit("update-members");
        }
        this.deActivateLoading = false;
        this.showDeActivateModal = false;
      },

      async acceptMember(event, memberId) {
        if (event.target.innerText === "Accept") {
          this.id = memberId.split("/")[1];
          this.showAcceptModal = true;
          return;
        }
        this.acceptLoading = true;
        const payload = { id: this.id, action: "accept" };
        const response = await this.pendingInvite(payload);
        if (response) {
          this.$emit("update-members");
        }
        this.acceptLoading = false;
        this.showAcceptModal = false;
      },

      async declineMember(event, memberId) {
        if (event.target.innerText === "Decline") {
          this.id = memberId.split("/")[1];
          this.showDeclineModal = true;
          return;
        }
        this.declineLoading = true;
        const payload = { id: this.id, action: "reject" };
        const response = await this.pendingInvite(payload);
        if (response) {
          this.$emit("update-members");
        }
        this.declineLoading = false;
        this.showDeclineModal = false;
      },

      setTableColumn() {
        this.colunmSize = `repeat(${this.columnNumber}, ${
          this.columnWidth
        }px) ${120}px`;
        this.setColumnStatus();
        if (this.$refs.tableHead) {
          this.$refs.tableHead.style.gridTemplateColumns = this.colunmSize;
        }
        this.rows.forEach((row) => {
          if (row) {
            row.style.gridTemplateColumns = this.colunmSize;
          }
        });
      },

      setColumnStatus() {
        // Status styling is now handled via setStatusRef and CSS classes
        // This method is kept for backwards compatibility
        this.rows.forEach((row) => {
          if (!row) return;
          let columns = row?.querySelectorAll("p, span");
          if (columns && columns.length > 0) {
            const lastCol = columns[columns.length - 1];
            const text = lastCol?.innerText || lastCol?.textContent || '';
            if (text === "Active") {
              lastCol.classList.add("status-badge", "status-badge--active");
            } else if (text === "Deactivated") {
              lastCol.classList.add("status-badge", "status-badge--deactivated");
            } else if (text === "Pending") {
              lastCol.classList.add("status-badge", "status-badge--pending");
            }
          }
        });
      },

      gotoPage(page) {
        this.$emit("gotoPage", page);
      },
      
      setStatusRef(el, detail) {
        // Apply status class after DOM is rendered
        if (el && typeof detail === 'string') {
          const text = el.innerText || el.textContent || '';
          if (text === 'Active') {
            el.classList.add('status-badge', 'status-badge--active');
          } else if (text === 'Deactivated') {
            el.classList.add('status-badge', 'status-badge--deactivated');
          } else if (text === 'Pending') {
            el.classList.add('status-badge', 'status-badge--pending');
          }
        }
      },
      
      getStatusClass(detail) {
        // Check if detail contains status text
        if (typeof detail === 'string') {
          if (detail.includes('>Active<') || detail === 'Active') return 'status-badge status-badge--active';
          if (detail.includes('>Deactivated<') || detail === 'Deactivated') return 'status-badge status-badge--deactivated';
          if (detail.includes('>Pending<') || detail === 'Pending') return 'status-badge status-badge--pending';
        }
        return '';
      },
    },
    watch: {
      tableRows(newValue, oldValue) {
        if (newValue !== oldValue) {
          setTimeout(() => {
            this.setTableColumn();
          }, 100);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
.members-table-wrapper {
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  position: relative;
}

.members-table {
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  overflow: hidden;
  
  &__head {
    background: #F8FAFC;
  }
  
  &__th {
    padding: 16px 20px;
    text-align: left;
    font-size: 12px;
    font-weight: 500;
    color: #64748B;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid #E2E8F0;
    
    &--hidden {
      display: none;
    }
  }
  
  &__body {
    background: #ffffff;
  }
  
  &__row {
    transition: background 0.15s ease;
    
    &:hover {
      background: #F8FAFC;
    }
    
    &:not(:last-child) {
      border-bottom: 1px solid #F1F5F9;
    }
  }
  
  &__td {
    padding: 16px 20px;
    font-size: 14px;
    color: #0F172A;
    font-weight: 400;
    vertical-align: middle;
    
    &--hidden {
      display: none;
    }
    
    &--action {
      width: 60px;
      text-align: center;
    }
  }
  
  &__cell-content {
    display: inline-block;
  }
  
  &__pagination {
    display: flex;
    justify-content: flex-end;
    margin-top: 20px;
  }
}

// Status Badges
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  
  &--active {
    background: rgba(16, 185, 129, 0.1);
    color: #059669;
  }
  
  &--pending {
    background: rgba(245, 158, 11, 0.1);
    color: #D97706;
  }
  
  &--deactivated {
    background: rgba(239, 68, 68, 0.1);
    color: #DC2626;
  }
}

// Action Menu
.action-menu {
  position: relative;
  display: inline-flex;
  
  &__trigger {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    border-radius: 8px;
    color: #64748B;
    cursor: pointer;
    transition: all 0.15s ease;
    
    &:hover {
      background: #F1F5F9;
      color: #0F172A;
    }
  }
  
  &__dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    z-index: 50;
    min-width: 180px;
    padding: 8px;
    background: #ffffff;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    opacity: 0;
    visibility: hidden;
    transform: translateY(-8px);
    transition: all 0.2s ease;
  }
  
  &:hover &__dropdown {
    opacity: 1;
    visibility: visible;
    transform: translateY(4px);
  }
  
  &__item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px 12px;
    background: transparent;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 400;
    color: #0F172A;
    cursor: pointer;
    transition: all 0.15s ease;
    text-align: left;
    
    &:hover {
      background: #F1F5F9;
    }
    
    &--success {
      color: #059669;
      
      &:hover {
        background: rgba(16, 185, 129, 0.1);
      }
    }
    
    &--danger {
      color: #DC2626;
      
      &:hover {
        background: rgba(239, 68, 68, 0.1);
      }
    }
  }
}

// Modal Content
.modal-content {
  text-align: center;
  padding: 8px;
  
  &__icon {
    width: 56px;
    height: 56px;
    margin: 0 auto 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    
    &--success {
      background: rgba(16, 185, 129, 0.1);
      color: #059669;
    }
    
    &--danger {
      background: rgba(239, 68, 68, 0.1);
      color: #DC2626;
    }
  }
  
  &__title {
    font-size: 18px;
    font-weight: 500;
    color: #0F172A;
    margin-bottom: 8px;
  }
  
  &__description {
    font-size: 14px;
    color: #64748B;
    line-height: 1.5;
    margin-bottom: 24px;
  }
  
  &__actions {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
}

// Buttons
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &--primary {
    background: #4D84FF;
    color: #ffffff;
    
    &:hover {
      background: #3B6EE8;
    }
  }
  
  &--danger {
    background: #DC2626;
    color: #ffffff;
    
    &:hover {
      background: #B91C1C;
    }
  }
  
  &--ghost {
    background: transparent;
    color: #64748B;
    
    &:hover {
      color: #0F172A;
    }
  }
  
  &--full {
    width: 100%;
  }
  
  &__spinner {
    width: 20px;
    height: 20px;
  }
}

// Responsive
@media (max-width: 768px) {
  .members-table {
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    
    &__th,
    &__td {
      padding: 12px 16px;
      white-space: nowrap;
    }
  }
}
</style>
